import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useState } from 'react';
import {
  Card, Typography, Form, Select, Input, AutoComplete, Button, Space, Tag, Alert, message, Divider,
  Upload, List, Popconfirm, Empty,
} from 'antd';
import {
  RobotOutlined, SafetyCertificateOutlined, InboxOutlined, FileTextOutlined, DeleteOutlined,
} from '@ant-design/icons';
import {
  getAiConfig, saveAiConfig, clearAiConfig, AiConfig,
  PROVIDERS, modelsFor, DEFAULT_MODEL_BY_PROVIDER, KEY_PLACEHOLDER_BY_PROVIDER,
  listDocuments, uploadDocument, deleteDocument, HRDoc,
} from '../../services/aiConfigService';

const { Title, Paragraph, Text, Link } = Typography;

const AiAssistantPage: React.FC = () => {
  const [config, setConfig] = useState<AiConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [provider, setProvider] = useState<string>('anthropic');
  const [model, setModel] = useState<string>(DEFAULT_MODEL_BY_PROVIDER.anthropic);
  const [apiKey, setApiKey] = useState('');

  const [docs, setDocs] = useState<HRDoc[]>([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const loadDocs = async () => {
    setDocsLoading(true);
    try { setDocs(await listDocuments()); }
    catch { /* keep empty */ }
    finally { setDocsLoading(false); }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const d = await uploadDocument(file);
      message.success(`Indexed “${d.name}” (${d.chunk_count} chunks).`);
      loadDocs();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Could not index that document.');
    } finally {
      setUploading(false);
    }
    return false; // prevent antd default upload
  };

  const handleDeleteDoc = async (id: string) => {
    try { await deleteDocument(id); message.success('Document removed.'); loadDocs(); }
    catch { message.error('Could not remove the document.'); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const c = await getAiConfig();
      setConfig(c);
      const p = (c.provider || 'anthropic').toLowerCase();
      setProvider(p);
      setModel(c.model || DEFAULT_MODEL_BY_PROVIDER[p] || '');
    } catch {
      message.error('Could not load AI settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadDocs(); }, []);

  // Switching provider swaps the suggestion list; snap the model to the new
  // provider's default (models don't transfer across providers).
  const onProviderChange = (p: string) => {
    setProvider(p);
    setModel(DEFAULT_MODEL_BY_PROVIDER[p] || '');
  };

  const effectiveModel = model.trim();

  const save = async () => {
    if (!effectiveModel) { message.error('Please choose or enter a model.'); return; }
    setSaving(true);
    try {
      const c = await saveAiConfig({
        provider,
        model: effectiveModel,
        apiKey: apiKey.trim() || undefined, // omit to keep the existing key
      });
      setConfig(c);
      setApiKey('');
      message.success('AI settings saved.');
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not save AI settings.');
    } finally {
      setSaving(false);
    }
  };

  const removeKey = async () => {
    setSaving(true);
    try {
      const c = await clearAiConfig();
      setConfig(c);
      message.success('API key removed.');
    } catch {
      message.error('Could not remove the key.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <Title level={3}><RobotOutlined /> AI Assistant</Title>
      <Paragraph type="secondary">
        Connect your own AI provider key so your team can ask the assistant about your organization
        and leave. Usage is billed to your account. The key is stored encrypted and never shown again
        after saving.
      </Paragraph>

      <Card loading={loading}>
        {config && (
          <Alert
            style={{ marginBottom: 16 }}
            type={config.configured ? 'success' : 'info'}
            showIcon
            message={
              config.configured
                ? <span>API key configured <Tag>•••• {config.keyLast4}</Tag>{config.configuredAt ? ` · set ${fmtDate(config.configuredAt)}` : ''}</span>
                : 'No API key set yet — the assistant is disabled until you add one.'
            }
          />
        )}

        <Form layout="vertical">
          <Form.Item label="Provider" help="Pick your AI provider. Your key below must match it.">
            <Select value={provider} onChange={onProviderChange} options={PROVIDERS} />
          </Form.Item>

          <Form.Item label="Model" help="Models evolve — pick a current one or type any model id (new models work immediately).">
            <AutoComplete
              value={model}
              onChange={setModel}
              options={modelsFor(provider)}
              placeholder={`e.g. ${DEFAULT_MODEL_BY_PROVIDER[provider] || 'model id'}`}
              filterOption={(input, opt) =>
                String(opt?.value ?? '').toLowerCase().includes(input.toLowerCase()) ||
                String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              allowClear
            />
          </Form.Item>

          <Form.Item
            label="API key"
            help={config?.configured ? 'Leave blank to keep the current key. Enter a new key to replace it.' : `Paste your ${provider === 'openai' ? 'OpenAI' : 'Anthropic'} API key (${KEY_PLACEHOLDER_BY_PROVIDER[provider]}).`}
          >
            <Input.Password
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={config?.configured ? '•••••••••• (unchanged)' : KEY_PLACEHOLDER_BY_PROVIDER[provider]}
              autoComplete="off"
            />
          </Form.Item>

          <Space>
            <Button type="primary" loading={saving} onClick={save}>Save</Button>
            {config?.configured && <Button danger loading={saving} onClick={removeKey}>Remove key</Button>}
          </Space>
        </Form>

        <Divider />
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          <SafetyCertificateOutlined />{' '}
          <Text strong>Safety:</Text> the assistant is restricted to your company's HR and organization
          questions, and can only access data within each user's reporting hierarchy. Get a key from{' '}
          <Link href="https://console.anthropic.com/" target="_blank">console.anthropic.com</Link>.
        </Paragraph>
      </Card>

      <Card title={<span><FileTextOutlined /> Knowledge base</span>} style={{ marginTop: 24 }}>
        <Paragraph type="secondary">
          Upload your company's HR documents — employee handbook, leave policy, contracts. The assistant
          searches these to answer policy questions, and cites them. Documents are private to your company.
        </Paragraph>

        <Upload.Dragger
          multiple={false}
          accept=".pdf,.txt,.md"
          showUploadList={false}
          disabled={uploading}
          beforeUpload={(file) => handleUpload(file as File)}
        >
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">{uploading ? 'Indexing…' : 'Click or drag a file to upload'}</p>
          <p className="ant-upload-hint">PDF, TXT or Markdown.</p>
        </Upload.Dragger>

        <div style={{ marginTop: 16 }}>
          {docs.length === 0 && !docsLoading ? (
            <Empty description="No documents yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <List
              loading={docsLoading}
              dataSource={docs}
              renderItem={(d) => (
                <List.Item
                  actions={[
                    <Popconfirm key="del" title="Remove this document?" onConfirm={() => handleDeleteDoc(d.id)}>
                      <Button danger type="text" icon={<DeleteOutlined />} />
                    </Popconfirm>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={<FileTextOutlined style={{ fontSize: 18, color: '#fa8c16' }} />}
                    title={d.name}
                    description={<Space size="small"><Tag>{d.chunk_count} chunks</Tag><Tag color={d.status === 'ready' ? 'green' : 'red'}>{d.status}</Tag></Space>}
                  />
                </List.Item>
              )}
            />
          )}
        </div>
      </Card>
    </div>
  );
};

export default AiAssistantPage;
